import React, { useState } from "react"
import { CardBody, Col, Form, FormGroup, Label, Row } from "reactstrap"
import { useFormik } from "formik"

import { Modal } from "components/Modal"
import { FIELDS_NAME, initialValues, stageValidationSchema } from "./FormConfig"
import { TextInput } from "components/TextInput"
import FormikErrorText from "components/FormikErrorText/FormikErrorText"
import { Button } from "components/Button"
import { AutoComplete } from "components/AutoComplete"
import { AddProductionStages, GetDepartmentsListing } from "api/api.service"
import { AutoCompleteStyling, stageHoursSelectOptions } from "./FormConstants"
import useAsyncSelect from "hooks/useAsyncSelect"

const AddUpdateStageModal = props => {
   const {
      isOpen = false,
      handleModalToggling = () => {},
      stagesData = [],
      onSuccess = () => {},
      productionLineId = null,
   } = props

   let controller = new AbortController()
   let signal = controller.signal

   const [isLoading, setIsLoading] = useState(false)
   const [stageHours, setStageHours] = useState(null)
   const {
      selectEntity: stationListing,
      setSelectEntity: setStationsOption,
      handleGetSelectEntityData,
      handleSearchOnOptions,
   } = useAsyncSelect({
      apiFunc: GetDepartmentsListing,
      labelKey: "departmentName",
      valueKey: "id",
      dataKey: "data",
      defaultPage: 1,
   })

   const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
         ...initialValues,
      },
      validationSchema: stageValidationSchema,
      onSubmit: values => {
         handleSubmit(values)
      },
   })

   const handleSelectedStation = selectedStation => {
      formik.setFieldValue(FIELDS_NAME.STATION, selectedStation)
      formik.setFieldTouched(FIELDS_NAME.STATION, true, false)

      if (
         !selectedStation ||
         (Array.isArray(selectedStation) && !selectedStation.length)
      ) {
         formik.setFieldError(FIELDS_NAME.STATION, "Station is required")
         formik.setFieldValue(FIELDS_NAME.STATION, null)
      } else {
         formik.setErrors({
            ...formik.errors,
            [FIELDS_NAME.STATION]: null,
         })
      }
   }

   const handleSubmit = async values => {
      const { name, hours, station } = values

      const stationIds = station.map(station => station.id)

      const payload = {
         productionLineId: Number(productionLineId),
         stages: [
            ...stagesData,
            {
               name: name,
               sequence_number: stagesData.length + 1,
               hours: Number(hours.value),
               departmentIds: stationIds,
            },
         ],
      }

      setIsLoading(true)
      try {
         const res = await AddProductionStages(payload)
         if (res) {
            handleModalToggling(prev => !prev)
            formik.resetForm()
            setStageHours(null)
            onSuccess()
         }
      } catch (error) {
         console.error("Error adding production stage:", error)
      } finally {
         setIsLoading(false)
      }
   }

   const handleSelectStageOptions = option => {
      setStageHours(option)
      formik.setFieldValue(FIELDS_NAME.HOURS, option)
   }

   const handleModalClose = () => {
      formik.resetForm()
      setStageHours(null)
      handleModalToggling(prev => !prev)
   }

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="modal-header-container"
         backdrop="static"
         modalTitle={"Add Stage"}
         size="lg"
         bodyClassName="p-0"
      >
         <Form onSubmit={formik.handleSubmit}>
            <CardBody className="add-stage-card-container shadow-none">
               <Row className="mb-3 align-items-center">
                  <FormGroup className="d-flex user-validation-handler">
                     <Label
                        for={FIELDS_NAME.NAME}
                        className="add-stage-modal-label "
                        sm={3}
                     >
                        Name
                     </Label>
                     <Col sm={8} className="input-text-container ">
                        <TextInput
                           placeholder="Enter stage name"
                           name={FIELDS_NAME.NAME}
                           {...formik.getFieldProps(FIELDS_NAME.NAME)}
                        />

                        <FormikErrorText
                           formikInstance={formik}
                           fieldName={FIELDS_NAME.NAME}
                        />
                     </Col>
                  </FormGroup>
               </Row>

               <Row className="mb-3 align-items-center">
                  <FormGroup className="d-flex user-validation-handler">
                     <Label
                        for={FIELDS_NAME.HOURS}
                        className="add-stage-modal-label "
                        sm={3}
                     >
                        Select Hours
                     </Label>
                     <Col sm={8} className="input-text-container ">
                        <AutoComplete
                           value={stageHours}
                           isClearable
                           onChange={val => handleSelectStageOptions(val)}
                           isSearchable={false}
                           placeholder="Select hours"
                           customStyles={AutoCompleteStyling}
                           options={stageHoursSelectOptions}
                        />

                        <FormikErrorText
                           formikInstance={formik}
                           fieldName={FIELDS_NAME.HOURS}
                        />
                     </Col>
                  </FormGroup>
               </Row>

               <Row className="mb-3 align-items-center">
                  <FormGroup className="d-flex user-validation-handler">
                     <Label
                        for={FIELDS_NAME.STATION}
                        className="add-stage-modal-label "
                        sm={3}
                     >
                        Stations
                     </Label>
                     <Col sm={8} className="input-text-container ">
                        <AutoComplete
                           isMulti
                           handleBlur={() => {
                              setStationsOption(prev => {
                                 return {
                                    ...prev,
                                    page: 1,
                                    Q: "",
                                    data: [],
                                 }
                              })

                              handleGetSelectEntityData(1)
                           }}
                           onChange={handleSelectedStation}
                           onInputChange={val => {
                              handleSearchOnOptions(val)
                           }}
                           placeholder="Select station"
                           customStyles={AutoCompleteStyling}
                           options={stationListing?.data}
                           value={formik.values.station}
                           isLoading={stationListing?.fetching}
                           handleScroll={() => {
                              if (stationListing?.isDataAvailable) {
                                 handleGetSelectEntityData(stationListing?.page)
                              }
                           }}
                        />

                        <FormikErrorText
                           formikInstance={formik}
                           fieldName={FIELDS_NAME.STATION}
                        />
                     </Col>
                  </FormGroup>
               </Row>

               <div className="d-flex justify-content-center mt-5">
                  <Button
                     type="submit"
                     title={"Save"}
                     className="header-button"
                     isLoading={isLoading}
                  />
               </div>
            </CardBody>
         </Form>
      </Modal>
   )
}

export default AddUpdateStageModal
